/**
 * FILE: packages/utils/src/matchLevel.ts
 * PURPOSE: Shared job-eligibility utility.
 * AGENT_CONTEXT: Pure functions. No React, no Supabase client imports.
 * LAST_REVIEWED: 2026-08-12
 *
 * Eligibility is based on two hierarchical requirements:
 *   1. Qualification level  (student.qualification >= job.min_cleared_level)
 *   2. Experience band      (student.exp_band >= job.exp_band_required)
 *
 * Course is NOT used for eligibility.
 */

export type MatchLevel = 'strong_match' | 'not_eligible'

export interface JobRequirements {
  expBandRequired?: string | null
  minClearedLevel?: string | null
}

export interface CandidateProfile {
  expBand?: string | null
  qualification?: string | null
}

export interface EligibilityCheck {
  /** True only when every specified requirement is satisfied. */
  eligible: boolean
  /** 0, 50, or 100 depending on how many specified requirements are met. */
  score: number
  qualificationMet: boolean
  expBandMet: boolean
  /** User-facing reason when not eligible. */
  reason: string | null
}

const MESSAGES = {
  qualificationMismatch: 'You do not meet the required qualification criteria for this role.',
  expBandMismatch: 'You do not meet the required experience criteria for this role.',
  bothMismatch: 'You do not meet the minimum requirements for this role.',
}

/* --------------------------------------------------------------------------
 * Qualification hierarchy
 * ------------------------------------------------------------------------- */

const QUALIFICATION_LEVELS: Record<string, { track: string; level: number }> = {
  CFA_L1: { track: 'CFA', level: 1 },
  CFA_L2: { track: 'CFA', level: 2 },
  CFA_L3: { track: 'CFA', level: 3 },
  FRM_P1: { track: 'FRM', level: 1 },
  FRM_P2: { track: 'FRM', level: 2 },
  Financial_Modeling: { track: 'FM', level: 1 },
}

function parseQualificationCode(raw: string | null | undefined): { track: string; level: number; cleared: boolean } | null {
  if (!raw?.trim()) return null

  // Normalize: "CFA Level 1" → "CFA_L1", "CFA L1" → "CFA_L1", etc.
  let normalized = raw.trim().replace(/\s+/g, '_').replace(/-/g, '_')

  // Map common display variants to canonical codes before parsing suffixes.
  const displayVariants: Record<string, string> = {
    CFA_Level_1: 'CFA_L1',
    CFA_Level_2: 'CFA_L2',
    CFA_Level_3: 'CFA_L3',
    CFA_L1: 'CFA_L1',
    CFA_L2: 'CFA_L2',
    CFA_L3: 'CFA_L3',
    FRM_Part_1: 'FRM_P1',
    FRM_Part_2: 'FRM_P2',
    FRM_P1: 'FRM_P1',
    FRM_P2: 'FRM_P2',
    Financial_Modeling: 'Financial_Modeling',
    FinancialModeling: 'Financial_Modeling',
  }
  const mapped = displayVariants[normalized]
  if (mapped) {
    normalized = mapped
  }

  // Handle _STUDENT / _CLEARED suffixes (e.g. CFA_L1_STUDENT).
  const suffixMatch = normalized.match(/^(.+)_(STUDENT|CLEARED)$/i)
  const baseCode = suffixMatch?.[1] ?? normalized
  const suffix = suffixMatch?.[2]
  const cleared = suffix ? suffix.toUpperCase() === 'CLEARED' : true

  const meta = QUALIFICATION_LEVELS[baseCode]
  if (!meta) return null

  return { ...meta, cleared }
}

function qualificationMeetsRequirement(
  candidateQualification: string | null | undefined,
  requiredQualification: string | null | undefined
): boolean {
  if (!requiredQualification?.trim()) return true
  const required = parseQualificationCode(requiredQualification)
  if (!required) return true

  const candidate = parseQualificationCode(candidateQualification)
  if (!candidate) return false

  // Different tracks never satisfy each other.
  if (candidate.track !== required.track) return false

  // Higher level always satisfies lower requirement.
  if (candidate.level > required.level) return true
  if (candidate.level < required.level) return false

  // Same level: any variant satisfies (STUDENT or CLEARED).
  return true
}

/* --------------------------------------------------------------------------
 * Experience band hierarchy
 * ------------------------------------------------------------------------- */

export const EXP_BAND_ORDER: Record<string, number> = {
  '0-1': 0,
  '1-3': 1,
  '3-5': 2,
  '5-10': 3,
  '10+': 4,
}

function normalizeExpBand(raw: string | null | undefined): string {
  if (!raw?.trim()) return ''
  const cleaned = raw.trim().toLowerCase().replace(/\s/g, '')
  if (cleaned === '5+' || cleaned === '5plus' || cleaned === '10plus') return cleaned === '5plus' ? '5-10' : '10+'
  return cleaned
}

function expBandOrder(raw: string | null | undefined): number | null {
  const band = normalizeExpBand(raw)
  if (!band) return null
  return EXP_BAND_ORDER[band] ?? null
}

function expBandMeetsRequirement(
  candidateExpBand: string | null | undefined,
  requiredExpBands: string | null | undefined
): boolean {
  const tokens = parseRequiredExpBands(requiredExpBands)
  if (!tokens.length) return true

  const candidateOrder = expBandOrder(candidateExpBand)
  if (candidateOrder === null) return false

  // The minimum required band is the one with the lowest order index.
  const minRequiredOrder = Math.min(...tokens.map(expBandOrder).filter((o): o is number => o !== null))
  return candidateOrder >= minRequiredOrder
}

function parseRequiredExpBands(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return []
  return raw
    .split(',')
    .map((s) => normalizeExpBand(s))
    .filter(Boolean)
}

/* --------------------------------------------------------------------------
 * Public API
 * -------------------------------------------------------------------------- */

export function checkEligibility(job: JobRequirements, candidate: CandidateProfile): EligibilityCheck {
  const qualificationReqSpecified = Boolean(parseQualificationCode(job.minClearedLevel))
  const expBandReqSpecified = parseRequiredExpBands(job.expBandRequired).length > 0

  if (!qualificationReqSpecified && !expBandReqSpecified) {
    return {
      eligible: true,
      score: 100,
      qualificationMet: true,
      expBandMet: true,
      reason: null,
    }
  }

  const qualificationMet = qualificationMeetsRequirement(candidate.qualification, job.minClearedLevel)
  const expBandMet = expBandMeetsRequirement(candidate.expBand, job.expBandRequired)

  const specifiedCount = (qualificationReqSpecified ? 1 : 0) + (expBandReqSpecified ? 1 : 0)
  const metCount =
    (qualificationMet && qualificationReqSpecified ? 1 : 0) + (expBandMet && expBandReqSpecified ? 1 : 0)
  const score = specifiedCount === 0 ? 100 : Math.round((metCount / specifiedCount) * 100)
  const eligible = metCount === specifiedCount

  let reason: string | null = null
  if (!eligible) {
    if (!qualificationMet && !expBandMet) {
      reason = MESSAGES.bothMismatch
    } else if (!qualificationMet) {
      reason = MESSAGES.qualificationMismatch
    } else {
      reason = MESSAGES.expBandMismatch
    }
  }

  return { eligible, score, qualificationMet, expBandMet, reason }
}

/**
 * @deprecated Use `checkEligibility` for new code.
 * Kept for backwards compatibility with callers that only need a match level.
 */
export function calculateMatchLevel(job: JobRequirements, candidate: CandidateProfile): MatchLevel {
  return checkEligibility(job, candidate).eligible ? 'strong_match' : 'not_eligible'
}

export function calculateMatchScore(job: JobRequirements, candidate: CandidateProfile): number {
  return checkEligibility(job, candidate).score
}

export function getEligibilityReason(job: JobRequirements, candidate: CandidateProfile): string | null {
  return checkEligibility(job, candidate).reason
}
